import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import type {
  DefectPrimaryKeyId,
  Endpoint,
  GuidId,
  IApiResponse,
  IHttpDefect,
  IHttpEditDefect,
  IHttpGetClientProject,
  IHttpGetSite,
  ListPrimaryKeyId,
  PrimaryKeyId,
  ProjectPrimaryKeyId,
} from '../../models';
import { HttpService } from '../http.service';

import {
  ICreateDefect,
  ICreateDefectPhoto,
  IEditDefect,
  IEditDefectPhoto,
  IHttpCreateDefect,
} from '../../models';

import { HTTP_METHOD } from '.';
import { ClientUserService } from '../client-user.service';

const CLIENT_REGISTER_ENDPOINT = 'ClientRegister' as Endpoint;
const CLIENT_PROJECT_ENDPOINT = 'ClientProject' as Endpoint;
const GET_SITE_ENDPOINT = 'GetSite' as Endpoint;
const GET_DEFECT_ENDPOINT = 'GetDefect' as Endpoint;
const CREATE_DEFECT_ENDPOINT = 'CreateDefect' as Endpoint;
const EDIT_DEFECT_ENDPOINT = 'EditDefect' as Endpoint;

@Injectable({
  providedIn: 'root',
})
export class ClientApiService {
  constructor(
    private readonly http: HttpService,
    private readonly cus: ClientUserService,
  ) {}

  /**
   * Creates a client user with the given email, first name, last name, and Firebase ID.
   *
   * @param {string} firstname - The first name of the user.
   * @param {string} lastname - The last name of the user.
   * @return {Promise<IApiResponse<undefined>>} A promise that resolves to the API response
   */
  createClientUser(
    firstname: string,
    lastname: string,
    phone: string,
    role: string | 'manager' | 'owner',
  ): Observable<IApiResponse<undefined>> {
    return this.http.request$<undefined>(
      HTTP_METHOD.POST,
      CLIENT_REGISTER_ENDPOINT,
      {
        body: {
          firstname,
          lastname,
          phone,
          role,
        },
      },
    );
  }

  /**
   * Gets the details of a project
   * @param {ProjectPrimaryKeyId} projectId cloud id of project
   * @returns {Promise<IApiResponse<IHttpGetClientProject>>} Project data
   */
  getProject(
    projectId: ProjectPrimaryKeyId,
  ): Observable<IApiResponse<IHttpGetClientProject>> {
    return this.http.request$<IHttpGetClientProject>(
      HTTP_METHOD.GET,
      CLIENT_PROJECT_ENDPOINT,
      {
        params: { projectId },
      },
    );
  }

  /**
   * Returns the site details and defects for a site
   * @param listId Cloud Id of site to get
   * @param {GuidId} projectGuid
   * @returns {Promise<IApiResponse<IHttpGetSite>>}
   */
  getList(
    listId: ListPrimaryKeyId,
    projectId: ProjectPrimaryKeyId,
    projectGuid: GuidId,
  ): Observable<IApiResponse<IHttpGetSite>> {
    return this.cus.getTenantId().pipe(
      switchMap(({ tenantId }) => {
        return this.http.request$<IHttpGetSite>(
          HTTP_METHOD.GET,
          GET_SITE_ENDPOINT,
          {
            params: {
              tenantId,
              projectId,
              projectGuid,
              siteId: listId,
            },
          },
        );
      }),
    );
  }

  /**
   * Gets the details of an individual defect
   * @param {DefectPrimaryKeyId} defectId raw Id of the defect
   * @param {GuidId} projectGuid
   * @returns
   */
  getDefect(
    defectId: DefectPrimaryKeyId,
    projectGuid: GuidId,
  ): Observable<IApiResponse<IHttpDefect>> {
    return this.cus.getTenantId().pipe(
      switchMap(({ tenantId }) => {
        return this.http.request$<IHttpDefect>(
          HTTP_METHOD.GET,
          GET_DEFECT_ENDPOINT,
          {
            params: {
              tenantId,
              defectId,
              projectGuid,
            },
          },
        );
      }),
    );
  }

  /**
   * Creates a new defect
   * @param {ICreateDefect} defect Data for new defect
   * @param {IPhoto[]} photos
   * @param {GuidId} projectGuid
   * @returns {Promise<IApiResponse<IApiUpdateResponse>>} New cloud Id and lastmodified
   */
  createDefect(
    defect: ICreateDefect,
    photos: ICreateDefectPhoto[],
    projectId: ProjectPrimaryKeyId,
    projectGuid: GuidId,
    contractorIds: PrimaryKeyId[],
  ): Observable<IApiResponse<IHttpCreateDefect>> {
    return this.cus.getTenantId().pipe(
      switchMap(({ tenantId }) =>
        this.http.request$<IHttpCreateDefect>(
          HTTP_METHOD.POST,
          CREATE_DEFECT_ENDPOINT,
          {
            body: {
              tenantId,
              projectId,
              projectGuid,
              defect,
              contractorIds,
              photos,
            },
          },
        ),
      ),
    );
  }

  /**
   * Update an existing defect
   * @param {IEditDefect} defect Defect data to update
   * @param {IEditDefectPhoto[]} photos
   * @param {number} projectId Cloud Id of the project
   * @returns {Promise<IApiResponse<IApiUpdateResponse>>} CloudId and lastmodified
   */
  editDefect(
    defect: IEditDefect,
    photos: IEditDefectPhoto[],
    projectId: ProjectPrimaryKeyId,
    projectGuid: GuidId,
  ): Observable<IApiResponse<IHttpEditDefect>> {
    return this.cus.getTenantId().pipe(
      switchMap(({ tenantId }) =>
        this.http.request$<IHttpEditDefect>(
          HTTP_METHOD.PUT,
          EDIT_DEFECT_ENDPOINT,
          {
            body: {
              tenantId,
              projectId,
              projectGuid,
              defect,
              contractors: [],
              photos,
            },
          },
        ),
      ),
    );
  }
}
